import { Bounce, Elastic, Expo, Sine, TimelineLite, TimelineMax } from "gsap";
import * as HLF from "html-living-framework";
import { Emitter } from "pixi-particles";
import * as particlesConfig from "../../json/pinata-particles.json";
import { Application } from "../Application";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { GalleryPaintableData } from "../Data/GalleryPaintableData";
import { GameType } from "../Data/GameData";
import * as PinataData from "../Data/PinataData";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOver";
import { PaintablePinata } from "../GameObjects/PaintablePinata";
import { GamePaintable } from "../States/GamePaintable";

export class GamePinata extends GamePaintable {
    protected readonly _gameType = GameType.PINATA;
    protected readonly _colours: number[] = [
        // Day of the Dead
        (new HLF.Colour(255, 245, 91)).toHex(), // Yellow
        (new HLF.Colour(255, 167, 50)).toHex(), // Orange
        (new HLF.Colour(255, 98, 81)).toHex(), // Red
        (new HLF.Colour(234, 125, 235)).toHex(), // Pink
        (new HLF.Colour(164, 91, 255)).toHex(), // Purple
        (new HLF.Colour(174, 255, 109)).toHex(), // Green
        (new HLF.Colour(68, 197, 239)).toHex(), // Cyan
        (new HLF.Colour(70, 22, 217)).toHex(), // Blue
        (new HLF.Colour(242, 238, 226)).toHex(), // Bone
        (new HLF.Colour(20, 25, 20)).toHex(), // Black
        // Chinese Opera
        (new HLF.Colour(223, 207, 48)).toHex(), // Yellow
        (new HLF.Colour(228, 133, 53)).toHex(), // Orange
        (new HLF.Colour(227, 135, 150)).toHex(), // Pink
        (new HLF.Colour(232, 32, 32)).toHex(), // Red
        (new HLF.Colour(56, 191, 88)).toHex(), // Green
        (new HLF.Colour(42, 115, 226)).toHex(), // Blue
        (new HLF.Colour(255, 255, 255)).toHex(), // White
        (new HLF.Colour(0, 0, 0)).toHex(), // Black
    ];
    protected _swapHintTween: TimelineMax | undefined;
    protected _pinataHintTween: TimelineMax | undefined;
    protected _pinataSwingTimeline: TimelineLite | undefined;
    protected _pinataHitCount: number = 0;
    protected _pinataParticles: Emitter | undefined;
    private _funFactsDisabled: boolean = false;

    constructor() {
        super();
        this._funFactCommon = VoiceOver.FUN_FACT_PINATA_COMMON;
        this._funFactUnique = VoiceOver.FUN_FACT_PINATA_UNIQUE;

        this._palette.setColours(this._colours);

        for (let i = 0; i < PinataData.LAYER_DATA.length; ++i) {
            const pinata = new PaintablePinata(i);
            pinata.addRope();
            this._paintables.push(pinata);
            const data: GalleryPaintableData = AppProgress.games[this._gameType].saveData[i] as GalleryPaintableData;
            if (data !== undefined) {
                for (let j = 0; j < data.sequence.length; ++j) {
                    this._paintables[i].layers[data.sequence[j]].paint(
                        data.colours[data.sequence[j]],
                        0, 0, true, true,
                    );
                }
            }
            this.addChild(this._paintables[i]);
            this.setChildIndex(this._paintables[i], 4);
        }

        HLF.PixiUtils.sendToFront(this._andy);
        HLF.PixiUtils.sendToFront(this._buttonSubmit);
        HLF.PixiUtils.sendToFront(this._buttonClear);
        HLF.PixiUtils.sendToFront(this._onboardingManager);
        HLF.PixiUtils.sendToFront(this._zoomOnboarding);
        HLF.PixiUtils.sendToFront(this._keyboardFocusManager);

        this._pubSubTokens.push(PubSub.subscribe(
            Topics.PINATA_HIT,
            this.onPinataHit.bind(this),
        ));
    }

    public static getRequiredAssets(): string[] {
        const required = super.getRequiredAssets();
        for (let i = 0; i < PinataData.LAYER_DATA.length; ++i) {
            required.push(Constants.SPRITESHEET_PINATA_PREFIX + "0" + i + HLF.ResourceUtils.FILE_EXTENSION_JSON);
        }
        required.push(
            "pinata00-mask-left.png",
            "pinata01-mask-left.png",
            "pinata-candy.json",
        );
        return required;
    }

    public init(pSize: PIXI.Point): void {
        super.init(pSize);

        if (this._isOnboarding === false) {
            this._activePaintableIndex = HLF.Random.intBetween(0, PinataData.LAYER_DATA.length);
            this._currentFunFactIndex = Math.floor(this._activePaintableIndex / 3);
        }
        for (let i = 0; i < PinataData.LAYER_DATA.length; ++i) {
            this._paintables[i].visible = (i === this._activePaintableIndex);
        }
    }

    public animateOut(pOnComplete: () => void): void {
        super.animateOut(() => {
            pOnComplete();
        });
    }

    protected onPinataHit(): void {
        this.stopPinataHint();
        if (this._pinataHitCount < 2) {
            this.animatePinataSwing();
            this._pinataHitCount++;
        }
        else {
            this.animatePinataSmash();
        }
    }

    protected animatePinataSwing(): void {
        // pivot top center for pinata swing
        this.activePaintable.pivot.set(0, -this.activePaintable.height / 2);
        this.activePaintable.position.set(0, -this.activePaintable.height / 2);
        if (this._pinataSwingTimeline !== undefined) {
            this._pinataSwingTimeline.clear();
        }
        this._pinataSwingTimeline = new TimelineLite();

        // swing right or left, then opposite, then back to center
        const direction = this._pinataHitCount % 2 === 0 ? 1 : -1;
        this._pinataSwingTimeline.to(this.activePaintable, 0.25, {
            rotation: -45 * direction * PIXI.DEG_TO_RAD,
            ease: Sine.easeOut,
        }).to(this.activePaintable, 0.5, {
            rotation: 22.5 * direction * PIXI.DEG_TO_RAD,
            ease: Sine.easeInOut,
        }).to(this.activePaintable, 2.5, {
            rotation: 0 * PIXI.DEG_TO_RAD,
            ease: Elastic.easeOut.config(0.5, 0.2),
        }).call(() => {
            this.playPinataHint();
        });
    }

    protected animatePinataSmash(): void {
        // pivot bottom right (of visible left half) for pinata smash
        this.activePaintable.pivot.set(this.activePaintable.width / 2, this.activePaintable.height / 2);
        this.activePaintable.position.set(this.activePaintable.width / 2, this.activePaintable.height / 2);

        if (this._pinataSwingTimeline !== undefined) {
            this._pinataSwingTimeline.kill();
        }
        this._pinataSwingTimeline = new TimelineLite();
        (this.activePaintable as PaintablePinata).setInputEnabledPinata(false);
        (this.activePaintable as PaintablePinata).hideRope();
        PubSub.publishSync(Topics.KEYBOARD_REFOCUS, undefined);

        // duplicate pinata, mask left
        const clonedTexture = Application.instance.renderer.generateTexture(
            this.activePaintable,
            undefined,
            Application.instance.renderer.resolution,
        );

        const clonedPinata = new PIXI.Sprite(clonedTexture);
        clonedPinata.anchor.set(0.5, 0.5);
        // pivot bottom left (of visible right half) for pinata smash
        clonedPinata.pivot.set(-clonedPinata.width / 2, clonedPinata.height / 2);
        clonedPinata.position.set(-clonedPinata.width / 2, clonedPinata.height / 2);
        this.addChild(clonedPinata);

        const maskTextureName = this._activePaintableIndex === 0 ?  "pinata00-mask-left" : "pinata01-mask-left";

        const maskLeft = this.activePaintable.addChild(HLF.PixiUtils.makeSprite(maskTextureName));
        this.activePaintable.mask = maskLeft;

        // mask right with invert of mask left
        const maskRight = clonedPinata.addChild(HLF.PixiUtils.makeSprite(maskTextureName));
        const invertFilter = new PIXI.filters.ColorMatrixFilter();
        invertFilter.negative(false);
        maskRight.filters = [invertFilter];
        // bit of a hack to get the mask to work
        const maskRightTexture = Application.instance.renderer.generateTexture(
            maskRight,
            undefined,
            Application.instance.renderer.resolution,
        );
        maskRight.texture = maskRightTexture;
        clonedPinata.mask = maskRight;

        this._pinataSwingTimeline.to(this.activePaintable, 1.5, {
            rotation: -15 * PIXI.DEG_TO_RAD,
            y: this.activePaintable.y - 200,
            x: this.activePaintable.x - 200,
            ease: Expo.easeOut,
        }, 0)
        .to(clonedPinata, 1.5, {
            rotation: 15 * PIXI.DEG_TO_RAD,
            y: clonedPinata.y - 200,
            x: clonedPinata.x + 200,
            ease: Expo.easeOut,
        }, 0);

        const particleContainer = this.addChild(new PIXI.Container());

        this._pinataParticles = new Emitter(particleContainer, [
            PIXI.Texture.fromImage("pinata-candy-green"),
            PIXI.Texture.from("pinata-candy-yellow"),
            PIXI.Texture.from("pinata-candy-blue"),
            PIXI.Texture.from("pinata-candy-red"),
            PIXI.Texture.from("pinata-candy-orange"),
            PIXI.Texture.from("pinata-candy-purple"),
        ], particlesConfig.default);

        this._pinataParticles.emitterLifetime = 4;
        this._pinataParticles.playOnceAndDestroy();

        // instead of dealing with the position tweens in animateOut and resetZoom, just fade it out
        this._pinataSwingTimeline.to([this.activePaintable, clonedPinata], 0.7, {alpha: 0}, 2);
        this._pinataSwingTimeline.call(() => {
            // go to next screen
            PubSub.publishSync(HLF.STATE_TRANSITION, new HLF.StateToken(
                Constants.STATE_MENU,
                Constants.LOAD_INTERSTITIAL,
            ));
            PubSub.publishSync(HLF.POP_KEYBOARD_LAYER, undefined);
        }, undefined, undefined, 3);

        Application.instance.analytics.event("activity", "complete", GameType[this._gameType]);
    }

    protected onOnboardingComplete(): void {
        super.onOnboardingComplete();
        this.playSwapHint();
    }

    protected onNextPressed(): void {
        super.onNextPressed();
        this.stopSwapHint();
    }

    protected onPrevPressed(): void {
        super.onPrevPressed();
        this.stopSwapHint();
    }

    protected playFunFact(cb?: () => void): void {
        if (this._funFactsDisabled) {
            return;
        }
        super.playFunFact();
    }

    protected onSubmitPressed(): void {
        // intentionally not calling super.onSubmitPressed();
        AppProgress.recentGame = this._gameType;
        this.saveData();

        this.activePaintable.setInputEnabled(false);
        this.setButtonsInteractive(false);
        this.setTouchControlsEnabled(false);
        this.resetZoom();
        this.disableFunFacts();

        const timeline: TimelineLite = new TimelineLite();
        // Tween out palette
        timeline.to(this._palette.position, 0.5, {
            x: -this._size.x * 0.5 - this._palette.width,
            ease: Sine.easeIn,
            onComplete: () => this._palette.visible = false,
        }, 0);
        // Tween out buttons
        timeline.to(this._buttonClear.position, 0.5, {
            x: this._size.x * 0.5 + this._buttonClear.width,
            ease: Sine.easeIn,
        }, 0);
        timeline.to(this._buttonNext.position, 0.5, {
            x: this._size.x * 0.5 + this._buttonNext.width,
            ease: Sine.easeIn,
        }, 0.1);
        timeline.to(this._buttonPrev.position, 0.5, {
            x: this._size.x * 0.5 + this._buttonPrev.width,
            ease: Sine.easeIn,
        }, 0.2);
        timeline.to(this._buttonSubmit.position, 0.5, {
            x: this._size.x * 0.5 + this._buttonSubmit.width,
            ease: Sine.easeIn,
        }, 0.3);

        PubSub.publishSync(HLF.PUSH_KEYBOARD_LAYER, undefined);
        (this.activePaintable as PaintablePinata).setInputEnabledPinata(true);
        this.playPinataHint();
    }

    protected incrementIndex(pStep: number): void {
        super.incrementIndex(pStep);
        this._currentFunFactIndex = Math.floor(this._activePaintableIndex / 3);
    }

    private disableFunFacts() {
        this._funFactsDisabled = true;
    }

    private playSwapHint(): void {
        this.stopSwapHint();

        this._swapHintTween = new TimelineMax({
            repeat: -1,
        });
        this._swapHintTween.add(this.createButtonHintTween(this._buttonNext), 5.0);
        this._swapHintTween.add(this.createButtonHintTween(this._buttonPrev), 5.2);
    }

    private playPinataHint(): void {
        this.stopPinataHint();
        // pivot center for hint
        this.activePaintable.pivot.set(0, 0);
        this.activePaintable.position.set(0, 0);
        this._pinataHintTween = new TimelineMax({
            repeat: -1,
        });
        this._pinataHintTween.add(this.createButtonHintTween(this.activePaintable), 1);
    }

    private createButtonHintTween(pTarget: PIXI.DisplayObject): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        timeline.to(pTarget.scale, 0.15, {
            x: 1.15,
            y: 1.15,
            ease: Sine.easeInOut,
        });
        timeline.to(pTarget.scale, 0.1, {
            x: 0.9,
            y: 0.9,
            ease: Sine.easeOut,
        });
        timeline.to(pTarget.scale, 1.0, {
            x: 1.0,
            y: 1.0,
            ease: Elastic.easeOut,
        });
        return timeline;
    }

    private stopSwapHint(): void {
        if (this._swapHintTween !== undefined) {
            this._swapHintTween.kill();
            this._buttonNext.scale.set(1);
            this._buttonPrev.scale.set(1);
        }
    }

    private stopPinataHint() {
        if (this._pinataHintTween !== undefined) {
            this._pinataHintTween.kill();
            this.activePaintable.scale.set(1);
            this.activePaintable.scale.set(1);
        }
    }
}
